import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api';
import { useAuthStore } from '../../store/authStore';
import { useToast } from '../../hooks/useToast';
import Layout from '../../components/Layout';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Badge } from '../../components/ui/Badge';
import { SlideOver } from '../../components/ui/SlideOver';

const Tenants = () => {
  const navigate = useNavigate();
  const { setToken, setUser, user } = useAuthStore();
  const { success, error: toastError, ToastContainer } = useToast();

  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [resendingInviteTenantId, setResendingInviteTenantId] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });
  
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [isImpersonating, setIsImpersonating] = useState(false);

  useEffect(() => {
    fetchTenants();
  }, [pagination.page, search, statusFilter]);

  const fetchTenants = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await api.get('/admin/tenants', {
        params: {
          page: pagination.page,
          limit: pagination.limit,
          search,
          status: statusFilter
        }
      });
      
      setTenants(response.data.tenants);
      setPagination(prev => ({
        ...prev,
        ...response.data.pagination
      }));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load tenants');
      console.error('Error fetching tenants:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleImpersonate = async (tenantId) => {
    try {
      setIsImpersonating(true);
      const response = await api.post(`/admin/impersonate/${tenantId}`);
      
      // Store the impersonation token
      setToken(response.data.token);
      setUser({
        ...response.data.user,
        isImpersonation: true,
        originalAdmin: user
      });
      
      // Redirect to tenant dashboard
      navigate('/tenant/dashboard');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to impersonate tenant');
      console.error('Impersonation error:', err);
    } finally {
      setIsImpersonating(false);
    }
  };

  const handleResendInvite = async (tenant) => {
    try {
      setResendingInviteTenantId(tenant.id);
      await api.post(`/admin/tenants/${tenant.id}/resend-invite`);
      success(
        'Invitation sent',
        tenant.email
          ? `Onboarding link emailed to ${tenant.email}.`
          : 'Onboarding invitation was sent.'
      );
    } catch (err) {
      toastError(
        'Could not send invitation',
        err.response?.data?.error || 'Failed to resend invitation.'
      );
      console.error('Resend invite error:', err);
    } finally {
      setResendingInviteTenantId(null);
    }
  };

  const canResendOnboardingInvite = (tenant) =>
    tenant.status === 'active' &&
    (tenant.user_count || 0) === 0 &&
    Boolean(tenant.email?.trim());

  const resendInviteDisabledTitle = (tenant) => {
    if (canResendOnboardingInvite(tenant)) return 'Resend onboarding invitation';
    if (tenant.status !== 'active') return 'Only active tenants can receive invites';
    if ((tenant.user_count || 0) > 0) return 'Tenant already has staff users';
    return 'Add a contact email first';
  };

  const handleDeleteTenant = async (tenantId, permanent = false) => {
    const confirmMessage = permanent 
      ? 'Are you sure you want to PERMANENTLY delete this tenant? This action cannot be undone!'
      : 'Are you sure you want to cancel this tenant?';
    
    if (!confirm(confirmMessage)) return;

    try {
      await api.delete(`/admin/tenants/${tenantId}${permanent ? '?permanent=true' : ''}`);
      fetchTenants(); // Refresh list
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete tenant');
      console.error('Delete error:', err);
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'suspended':
        return 'bg-yellow-100 text-yellow-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPlanBadgeClass = (planType) => {
    switch (planType) {
      case 'Free':
        return 'bg-gray-100 text-gray-800';
      case 'Pro Monthly':
        return 'bg-blue-100 text-blue-800';
      case 'Pro Annual':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <Layout>
      <ToastContainer />
      {/* Header */}
      <div className="bg-[var(--ds-surface-panel)] border-b border-[var(--ds-border-subtle)] shadow-[var(--ds-shadow-card)]">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="md:flex md:items-center md:justify-between">
            <div className="flex-1 min-w-0">
              <h1 className="text-display text-[var(--ds-text-primary)] font-bold">
                Tenant Management
              </h1>
              <p className="mt-1 text-meta text-[var(--ds-text-secondary)]">
                Manage all tenants, subscriptions, and system access
              </p>
            </div>
            <div className="mt-4 flex md:mt-0 md:ml-4">
              <Button
                variant="secondary"
                onClick={() => navigate('/admin/dashboard')}
                className="mr-3"
              >
                ← Back to Dashboard
              </Button>
              <Button
                variant="primary"
                onClick={() => setShowCreateModal(true)}
              >
                + Create Tenant
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-meta font-medium text-[var(--ds-text-primary)] mb-1">
                  Search
                </label>
                <Input
                  type="text"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPagination(prev => ({ ...prev, page: 1 }));
                  }}
                  placeholder="Search by name or email..."
                />
              </div>
              <div>
                <label className="block text-meta font-medium text-[var(--ds-text-primary)] mb-1">
                  Status
                </label>
                <Select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPagination(prev => ({ ...prev, page: 1 }));
                  }}
                >
                  <option value="">All Statuses</option>
                  <option value="active">Active</option>
                  <option value="suspended">Suspended</option>
                  <option value="cancelled">Cancelled</option>
                </Select>
              </div>
              <div className="flex items-end">
                <Button variant="secondary" onClick={fetchTenants} className="w-full">
                  🔄 Refresh
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Error Message */}
        {error && (
          <div className="bg-[var(--ds-status-danger-bg)] border border-[var(--ds-status-danger)] text-[var(--ds-status-danger)] px-4 py-3 rounded-[var(--ds-radius-md)] mb-4">
            {error}
          </div>
        )}

        {/* Tenants Table */}
        <Card className="overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--ds-accent-primary)]"></div>
              <p className="mt-2 text-[var(--ds-text-muted)]">Loading tenants...</p>
            </div>
          ) : tenants.length === 0 ? (
            <div className="p-8 text-center text-[var(--ds-text-muted)]">
              <p>No tenants found</p>
              <Button
                variant="ghost"
                onClick={() => setShowCreateModal(true)}
                className="mt-4"
              >
                Create your first tenant
              </Button>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="border-b border-[var(--ds-border-divider)]">
                      <th className="px-6 py-3 text-left text-meta text-[var(--ds-text-muted)] uppercase tracking-wider">
                        Tenant
                      </th>
                      <th className="px-6 py-3 text-left text-meta text-[var(--ds-text-muted)] uppercase tracking-wider">
                        Contact
                      </th>
                      <th className="px-6 py-3 text-left text-meta text-[var(--ds-text-muted)] uppercase tracking-wider">
                        Plan
                      </th>
                      <th className="px-6 py-3 text-left text-meta text-[var(--ds-text-muted)] uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-meta text-[var(--ds-text-muted)] uppercase tracking-wider">
                        Users
                      </th>
                      <th className="px-6 py-3 text-left text-meta text-[var(--ds-text-muted)] uppercase tracking-wider">
                        Created
                      </th>
                      <th className="px-6 py-3 text-right text-meta text-[var(--ds-text-muted)] uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {tenants.map((tenant) => (
                      <tr key={tenant.id} className="hover:bg-[var(--ds-control-field-bg)] border-b border-[var(--ds-border-divider)]">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              <div 
                                className="h-10 w-10 rounded-full flex items-center justify-center text-white font-bold"
                                style={{ backgroundColor: tenant.primary_color }}
                              >
                                {tenant.name.charAt(0).toUpperCase()}
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-body font-medium text-[var(--ds-text-primary)]">
                                {tenant.name}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-body text-[var(--ds-text-primary)]">{tenant.email}</div>
                          {tenant.phone && (
                            <div className="text-meta text-[var(--ds-text-muted)]">{tenant.phone}</div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Badge variant="neutral">{tenant.plan_type}</Badge>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Badge 
                            variant={
                              tenant.status === 'active' ? 'success' : 
                              tenant.status === 'suspended' ? 'warning' : 
                              tenant.status === 'cancelled' ? 'danger' : 
                              'neutral'
                            }
                          >
                            {tenant.status}
                          </Badge>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-body text-[var(--ds-text-muted)]">
                          {tenant.user_count || 0}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-body text-[var(--ds-text-muted)]">
                          {new Date(tenant.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-body font-medium">
                          <div className="flex flex-wrap items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleImpersonate(tenant.id)}
                              disabled={tenant.status !== 'active' || isImpersonating}
                              title="Impersonate tenant"
                            >
                              👤 View
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleResendInvite(tenant)}
                              disabled={
                                !canResendOnboardingInvite(tenant) ||
                                resendingInviteTenantId === tenant.id
                              }
                              title={resendInviteDisabledTitle(tenant)}
                            >
                              {resendingInviteTenantId === tenant.id ? 'Sending…' : '📨 Resend'}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedTenant(tenant);
                                setShowEditModal(true);
                              }}
                            >
                              ✏️ Edit
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteTenant(tenant.id, false)}
                              className="text-[var(--ds-status-danger)] hover:text-[var(--ds-status-danger)]"
                            >
                              🗑️ Cancel
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="bg-[var(--ds-surface-panel)] px-4 py-3 border-t border-[var(--ds-border-divider)] sm:px-6">
                  <div className="flex items-center justify-between">
                    <div className="text-meta text-[var(--ds-text-secondary)]">
                      Showing page <span className="font-medium">{pagination.page}</span> of{' '}
                      <span className="font-medium">{pagination.totalPages}</span>
                      {' '}({pagination.total} total tenants)
                    </div>
                    <div className="flex space-x-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                        disabled={pagination.page === 1}
                      >
                        Previous
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                        disabled={pagination.page === pagination.totalPages}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </Card>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <TenantFormModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            fetchTenants();
          }}
        />
      )}

      {/* Edit SlideOver */}
      <SlideOver
        open={showEditModal && !!selectedTenant}
        onOpenChange={(open) => {
          if (!open) {
            setShowEditModal(false);
            setSelectedTenant(null);
          }
        }}
        title={selectedTenant ? 'Edit Tenant' : ''}
      >
        {selectedTenant && (
          <TenantForm
            tenant={selectedTenant}
            onSuccess={() => {
              setShowEditModal(false);
              setSelectedTenant(null);
              fetchTenants();
            }}
          />
        )}
      </SlideOver>
    </Layout>
  );
};

// Tenant Form Component (reusable for both modal and slideover)
const TenantForm = ({ tenant, onClose, onSuccess }) => {
  const isEdit = !!tenant;
  
  // Parse feature_flags if it's a string (defensive)
  const featureFlags = useMemo(() => {
    if (!tenant?.feature_flags) return {};
    if (typeof tenant.feature_flags === 'string') {
      try {
        return JSON.parse(tenant.feature_flags);
      } catch {
        return {};
      }
    }
    return tenant.feature_flags || {};
  }, [tenant?.feature_flags]);

  const [formData, setFormData] = useState({
    name: tenant?.name || '',
    email: tenant?.email || '',
    phone: tenant?.phone || '',
    plan_type: tenant?.plan_type || 'Free',
    status: tenant?.status || 'active',
    primary_color: tenant?.primary_color || '#3B82F6',
    secondary_color: tenant?.secondary_color || '#10B981',
    certificates_enabled: tenant?.certificates_enabled !== undefined ? tenant.certificates_enabled : true,
    ai_purchases_enabled: featureFlags.ai_purchases_enabled === true,
    eicr_ai_enabled: featureFlags.eicr_ai_enabled === true,
    rams_enabled: featureFlags.rams_enabled === true
  });

  // Update form when tenant data changes
  useEffect(() => {
    if (tenant) {
      const flags = typeof tenant.feature_flags === 'string' 
        ? (() => { try { return JSON.parse(tenant.feature_flags); } catch { return {}; } })()
        : (tenant.feature_flags || {});
      
      setFormData({
        name: tenant.name || '',
        email: tenant.email || '',
        phone: tenant.phone || '',
        plan_type: tenant.plan_type || 'Free',
        status: tenant.status || 'active',
        primary_color: tenant.primary_color || '#3B82F6',
        secondary_color: tenant.secondary_color || '#10B981',
        certificates_enabled: tenant.certificates_enabled !== undefined ? tenant.certificates_enabled : true,
        ai_purchases_enabled: flags.ai_purchases_enabled === true,
        eicr_ai_enabled: flags.eicr_ai_enabled === true,
        rams_enabled: flags.rams_enabled === true
      });
    }
  }, [tenant]);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Ensure feature-flag booleans are always sent (even if false)
      const submitData = {
        ...formData,
        ai_purchases_enabled: formData.ai_purchases_enabled === true,
        eicr_ai_enabled: formData.eicr_ai_enabled === true,
        rams_enabled: formData.rams_enabled === true
      };

      console.log('Submitting form data:', submitData);

      if (isEdit) {
        await api.put(`/admin/tenants/${tenant.id}`, submitData);
      } else {
        await api.post('/admin/tenants', submitData);
      }
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.errors?.[0]?.msg || 'Failed to save tenant');
      console.error('Form error:', err);
      console.error('Error response:', err.response?.data);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="bg-[var(--ds-status-danger-bg)] border border-[var(--ds-status-danger)] text-[var(--ds-status-danger)] px-4 py-3 rounded-[var(--ds-radius-md)]">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <label className="block text-meta font-medium text-[var(--ds-text-primary)] mb-1">
            Business Name <span className="text-[var(--ds-status-danger)]">*</span>
          </label>
          <Input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            placeholder="e.g., Smith Electrical Ltd"
          />
        </div>

        <div>
          <label className="block text-meta font-medium text-[var(--ds-text-primary)] mb-1">
            Email <span className="text-[var(--ds-status-danger)]">*</span>
          </label>
          <Input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            placeholder="contact@smithelectrical.co.uk"
          />
        </div>

        <div>
          <label className="block text-meta font-medium text-[var(--ds-text-primary)] mb-1">
            Phone
          </label>
          <Input
            type="text"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            placeholder="01234 567890"
          />
        </div>

        <div>
          <label className="block text-meta font-medium text-[var(--ds-text-primary)] mb-1">
            Plan Type
          </label>
          <Select
            name="plan_type"
            value={formData.plan_type}
            onChange={handleChange}
          >
            <option value="Free">Free</option>
            <option value="Pro Monthly">Pro Monthly</option>
            <option value="Pro Annual">Pro Annual</option>
          </Select>
        </div>

        {isEdit && (
          <div>
            <label className="block text-meta font-medium text-[var(--ds-text-primary)] mb-1">
              Status
            </label>
            <Select
              name="status"
              value={formData.status}
              onChange={handleChange}
            >
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
              <option value="cancelled">Cancelled</option>
            </Select>
          </div>
        )}

        <div>
          <label className="block text-meta font-medium text-[var(--ds-text-primary)] mb-1">
            Primary Color
          </label>
          <div className="flex space-x-2">
            <input
              type="color"
              name="primary_color"
              value={formData.primary_color}
              onChange={handleChange}
              className="h-10 w-20 border border-[var(--ds-control-field-border)] rounded-[var(--ds-radius-md)]"
            />
            <Input
              type="text"
              value={formData.primary_color}
              onChange={(e) => setFormData(prev => ({ ...prev, primary_color: e.target.value }))}
              placeholder="#3B82F6"
            />
          </div>
        </div>

        <div>
          <label className="block text-meta font-medium text-[var(--ds-text-primary)] mb-1">
            Secondary Color
          </label>
          <div className="flex space-x-2">
            <input
              type="color"
              name="secondary_color"
              value={formData.secondary_color}
              onChange={handleChange}
              className="h-10 w-20 border border-[var(--ds-control-field-border)] rounded-[var(--ds-radius-md)]"
            />
            <Input
              type="text"
              value={formData.secondary_color}
              onChange={(e) => setFormData(prev => ({ ...prev, secondary_color: e.target.value }))}
              placeholder="#10B981"
            />
          </div>
        </div>

        <div className="md:col-span-2">
          <div className="flex items-center gap-3 rounded-[var(--ds-radius-md)] border border-[var(--ds-border-subtle)] bg-[var(--ds-control-field-bg)] px-4 py-3">
            <input
              type="checkbox"
              id="certificates_enabled"
              name="certificates_enabled"
              checked={formData.certificates_enabled}
              onChange={(e) => setFormData(prev => ({ ...prev, certificates_enabled: e.target.checked }))}
              className="h-4 w-4 text-[var(--ds-accent-primary)] border-[var(--ds-border-subtle)] rounded"
            />
            <label htmlFor="certificates_enabled" className="flex-1 cursor-pointer">
              <span className="block text-body font-medium text-[var(--ds-text-primary)]">
                Enable Certificates Feature
              </span>
              <span className="block text-meta text-[var(--ds-text-secondary)] mt-0.5">
                Allow this tenant to access the certificates section
              </span>
            </label>
          </div>
        </div>

        <div className="md:col-span-2">
          <div className="flex items-center gap-3 rounded-[var(--ds-radius-md)] border border-[var(--ds-border-subtle)] bg-[var(--ds-control-field-bg)] px-4 py-3">
            <input
              type="checkbox"
              id="ai_purchases_enabled"
              name="ai_purchases_enabled"
              checked={formData.ai_purchases_enabled}
              onChange={(e) => setFormData(prev => ({ ...prev, ai_purchases_enabled: e.target.checked }))}
              className="h-4 w-4 text-[var(--ds-accent-primary)] border-[var(--ds-border-subtle)] rounded"
            />
            <label htmlFor="ai_purchases_enabled" className="flex-1 cursor-pointer">
              <span className="block text-body font-medium text-[var(--ds-text-primary)]">
                Enable AI Purchase Processing
              </span>
              <span className="block text-meta text-[var(--ds-text-secondary)] mt-0.5">
                Allow this tenant to use AI-powered invoice processing for purchases
              </span>
            </label>
          </div>
        </div>

        <div className="md:col-span-2">
          <div className="flex items-center gap-3 rounded-[var(--ds-radius-md)] border border-[var(--ds-border-subtle)] bg-[var(--ds-control-field-bg)] px-4 py-3">
            <input
              type="checkbox"
              id="eicr_ai_enabled"
              name="eicr_ai_enabled"
              checked={formData.eicr_ai_enabled}
              onChange={(e) => setFormData(prev => ({ ...prev, eicr_ai_enabled: e.target.checked }))}
              className="h-4 w-4 text-[var(--ds-accent-primary)] border-[var(--ds-border-subtle)] rounded"
            />
            <label htmlFor="eicr_ai_enabled" className="flex-1 cursor-pointer">
              <span className="block text-body font-medium text-[var(--ds-text-primary)]">
                Enable AI Observation Helper (EICR)
              </span>
              <span className="block text-meta text-[var(--ds-text-secondary)] mt-0.5">
                Allow this tenant to use the AI-assisted observation helper when creating EICR observations
              </span>
            </label>
          </div>
        </div>

        <div className="md:col-span-2">
          <div className="flex items-center gap-3 rounded-[var(--ds-radius-md)] border border-[var(--ds-border-subtle)] bg-[var(--ds-control-field-bg)] px-4 py-3">
            <input
              type="checkbox"
              id="rams_enabled"
              name="rams_enabled"
              checked={formData.rams_enabled}
              onChange={(e) => setFormData(prev => ({ ...prev, rams_enabled: e.target.checked }))}
              className="h-4 w-4 text-[var(--ds-accent-primary)] border-[var(--ds-border-subtle)] rounded"
            />
            <label htmlFor="rams_enabled" className="flex-1 cursor-pointer">
              <span className="block text-body font-medium text-[var(--ds-text-primary)]">
                Enable RAMS
              </span>
              <span className="block text-meta text-[var(--ds-text-secondary)] mt-0.5">
                Allow this tenant to create and manage RAMS (Risk Assessments and Method Statements)
              </span>
            </label>
          </div>
        </div>
      </div>

      <div className="flex justify-end space-x-3 pt-4 border-t border-[var(--ds-border-divider)]">
        {onClose && (
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
        )}
        <Button
          type="submit"
          variant="primary"
          disabled={loading}
        >
          {loading ? 'Saving...' : (isEdit ? 'Update Tenant' : 'Create Tenant')}
        </Button>
      </div>
    </form>
  );
};

// Tenant Form Modal Component (for create)
const TenantFormModal = ({ tenant, onClose, onSuccess }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-[var(--ds-surface-panel)] border-b border-[var(--ds-border-subtle)] px-6 py-4">
          <div className="flex items-center justify-between">
            <h2 className="text-h1 font-bold text-[var(--ds-text-primary)]">
              Create New Tenant
            </h2>
            <button
              onClick={onClose}
              className="text-[var(--ds-text-muted)] hover:text-[var(--ds-text-primary)]"
            >
              ✕
            </button>
          </div>
        </div>
        <div className="p-6">
          <TenantForm onClose={onClose} onSuccess={onSuccess} />
        </div>
      </Card>
    </div>
  );
};

export default Tenants;

